import SendIcon from "@mui/icons-material/Send";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	Divider,
	Paper,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { SetCurrentRecipeModal } from "../../straightener-common/components/SetCurrentRecipeModal";
import { BANNER_CONTROL_HEIGHT } from "../../straightener-common/constants";
import { useStraightenerCanEdit } from "../../straightener-common/permissions";
import { buildProductNodes } from "../../straightener-common/productNodes";
import {
	STR_RECIPE_KEYS,
	useCurrentCrew,
	useSetStraightenerRecipeCurrent,
	useStraightenerProducts,
	useStraightenerRecipeValues,
	useStraightenerRecipes,
} from "../../straightener-recipes/hooks/useStraightenerRecipes";
import { withPreviewedRecipe } from "../compareRows";
import { useSendToPlc } from "../hooks/useSendToPlc";
import {
	STR_TREND_KEYS,
	useStraightenerTrend,
} from "../hooks/useStraightenerTrend";
import { InlineRecipeEditor } from "./InlineRecipeEditor";
import { ProductChangeModal } from "./ProductChangeModal";
import { RecipeActionsBar } from "./RecipeActionsBar";
import { SendToPlcConfirmModal } from "./SendToPlcConfirmModal";
import { SendToPlcModal } from "./SendToPlcModal";
import { RecipeSelect } from "./RecipeSelect";
import { StraightenerBanner } from "./StraightenerBanner";
import { StraightenerSetupGrid } from "./StraightenerSetupGrid";
import { StraightenerTrendChart } from "./StraightenerTrendChart";

// The real Send-to-PLC transfer stays off until the PLC write path (N200[] recipe
// registers + transfer trigger) is delivered by the client; until then the button
// opens a confirmation that says so. Flip to re-enable the transfer.
// See docs/features/straightener/DESIGN-NOTES.md.
const SHOW_SEND_TO_PLC = false;

const URL_SCHEMA = {
	// Only a recipe being previewed travels: the current one is whatever the line is set up
	// against, and pinning it in a link would freeze yesterday's answer.
	recipeId: urlParam.int(),
};

export const StraightenerTrendDisplay = () => {
	const [{ recipeId: previewedInUrl }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [editorMode, setEditorMode] = useState<"closed" | "edit" | "create">(
		"closed",
	);
	const [notification, setNotification] = useState("");
	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [confirmCurrentOpen, setConfirmCurrentOpen] = useState(false);
	const [productChangeOpen, setProductChangeOpen] = useState(false);
	const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
	const queryClient = useQueryClient();
	// Read Only users can't change the current recipe or open the editor.
	const canEdit = useStraightenerCanEdit();

	const crewQ = useCurrentCrew();

	// Recipe editing is always scoped to the selected product, so we pull the
	// full product + recipe catalog only to feed the inline editor (version list
	// for the current product, and copy-from sources).
	const productsQ = useStraightenerProducts();
	const recipesQ = useStraightenerRecipes();

	// Always show the current (PLC / campaign) recipe. The "Compare recipe" picker
	// is removed for now — see docs/features/straightener/DESIGN-NOTES.md.
	const { data, error } = useStraightenerTrend(null);
	const banner = data?.banner ?? null;

	// The product chosen with Product Change, resolved from the catalog so it carries Dim1/2/3
	// (Product Dimensions are auto-filled into the recipe from these).
	const product =
		banner?.productSizeId != null
			? ((productsQ.data ?? []).find((p) => p.Id === banner.productSizeId) ?? {
					Id: banner.productSizeId,
					Name: banner.productName,
					ProductTypeName: banner.productTypeName,
				})
			: null;

	const allNodes = useMemo(
		() => buildProductNodes(productsQ.data ?? [], recipesQ.data ?? []),
		[productsQ.data, recipesQ.data],
	);

	// Trend picks a recipe to make it current, so only active versions (plus the
	// current one) belong in the picker. Full history lives on the Recipes screen.
	const currentVersions = useMemo(
		() =>
			banner?.productSizeId != null
				? (
						allNodes.find((n) => n.productSizeId === banner.productSizeId)
							?.versions ?? []
					).filter((v) => v.IsActive || v.IsCurrent)
				: [],
		[allNodes, banner?.productSizeId],
	);

	const refreshRecipes = () => {
		queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
		queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
		// The save notification is shown by the editor's own snackbar — don't
		// duplicate it here.
	};

	// The product's default recipe, resolved by the SP from the product chosen with
	// Product Change — not whatever the picker is showing.
	const currentRecipeId = banner?.recipeId ?? null;
	// The picker shows the previewed recipe when there is one and the current recipe
	// otherwise, so nothing has to be seeded and the trend refetching every few seconds
	// cannot drop the recipe the operator is looking at.
	const selectedRecipeId = previewedInUrl ?? currentRecipeId;

	// The product changes, and with it the versions on offer. A link made for one
	// product must not keep previewing a recipe that does not belong to this one.
	useEffect(() => {
		if (previewedInUrl === null) return;
		// The recipe the line already runs is not a preview, however the link arrived.
		if (previewedInUrl === currentRecipeId) {
			dropValues("recipeId");
			return;
		}
		if (currentVersions.length === 0) return;
		if (!currentVersions.some((v) => v.Id === previewedInUrl))
			dropValues("recipeId");
	}, [previewedInUrl, currentRecipeId, currentVersions, dropValues]);

	// A recipe is being looked at without being applied.
	const previewRecipeId =
		selectedRecipeId != null && selectedRecipeId !== currentRecipeId
			? selectedRecipeId
			: null;
	const previewValuesQ = useStraightenerRecipeValues(previewRecipeId);
	const previewVersion = currentVersions.find((v) => v.Id === previewRecipeId);
	// What the Recipe column holds: the previewed recipe, or the default one.
	const selectedVersion = currentVersions.find(
		(v) => v.Id === selectedRecipeId,
	);
	const recipeLabel = selectedVersion
		? `${selectedVersion.Name ?? "(unnamed)"} · v${selectedVersion.Version}${
				selectedVersion.IsCurrent ? " (default)" : ""
			}`
		: currentRecipeId != null && banner
			? `${banner.name ?? "(unnamed)"} · v${banner.version} (default)`
			: null;
	const productLabel =
		banner?.productSizeId != null
			? `(${banner.productTypeName}) ${banner.productName}`
			: null;
	const rows = useMemo(
		() => withPreviewedRecipe(data?.rows ?? [], previewValuesQ.data),
		[data?.rows, previewValuesQ.data],
	);

	const editOpen = editorMode !== "closed";
	const editingRecipeId = editorMode === "create" ? null : selectedRecipeId;

	const { setCurrent, isSettingCurrent } = useSetStraightenerRecipeCurrent({
		onSuccess: () => {
			setNotification("Default recipe changed");
			// Back to following the current recipe, which is now the one just applied.
			dropValues("recipeId");
		},
		onError: (e) => setNotification(e.message),
	});

	// Send-to-PLC transfer state machine (kept inert while SHOW_SEND_TO_PLC is
	// false). Drives the confirmation modal; the trend rows feed the readback.
	const sendPlc = useSendToPlc({
		recipeId: currentRecipeId,
		rows: data?.rows ?? [],
	});
	const closeSendModal = () => {
		setSendModalOpen(false);
		sendPlc.reset();
	};
	const sendBusy =
		sendPlc.state === "sending" || sendPlc.state === "confirming";

	// Picking a recipe writes nothing: it loads that recipe's values into the
	// comparison column so the operator can see what applying it would change.
	// SET AS CURRENT is what applies it, and it asks first.
	const handleSelect = (recipeId: number) => {
		// Picking the recipe the line already runs is not a preview, so it leaves no key.
		if (recipeId === currentRecipeId) dropValues("recipeId");
		else setFilters({ recipeId });
		if (editorMode === "create") setEditorMode("edit");
	};
	const openEdit = () => setEditorMode("edit");
	const openCreate = () => setEditorMode("create");
	const closeEditor = () => setEditorMode("closed");
	const handleRecipeIdChange = (recipeId: number | null) => {
		if (recipeId != null) {
			if (recipeId === currentRecipeId) dropValues("recipeId");
			else setFilters({ recipeId });
			setEditorMode("edit");
		}
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 90px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Straightener Trend Display
			</Typography>
			<Paper
				elevation={3}
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					p: 2,
				}}
			>
				{error ? (
					<Alert severity="error" sx={{ mb: 1 }}>
						{(error as Error).message}
					</Alert>
				) : null}

				{/* The whole panel — banner included — waits for the first payload: an
				    empty banner renders as "Product not detected", which is a real
				    alarm state on this line, not a loading state. */}
				{!data ? (
					error ? null : (
						<Box
							sx={{
								flex: 1,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<CircularProgress />
						</Box>
					)
				) : (
					<>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
								mb: 1,
								flexWrap: "wrap",
							}}
						>
							<StraightenerBanner
								banner={banner}
								onProductChange={
									canEdit ? () => setProductChangeOpen(true) : undefined
								}
								recipeControl={
									product && canEdit && currentVersions.length > 0 ? (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												flexWrap: "wrap",
											}}
										>
											<RecipeSelect
												versions={currentVersions}
												valueId={
													editorMode === "create" ? null : selectedRecipeId
												}
												currentId={currentRecipeId}
												onSelect={handleSelect}
											/>
											{/* Send to PLC acts on the recipe the picker shows, so it
											    is the one action that stays next to the picker. */}
											{selectedRecipeId != null ? (
												<Button
													variant="contained"
													color="success"
													size="small"
													startIcon={<SendIcon />}
													disabled={SHOW_SEND_TO_PLC && sendBusy}
													onClick={() =>
														SHOW_SEND_TO_PLC
															? setSendModalOpen(true)
															: setSendConfirmOpen(true)
													}
													sx={{ height: BANNER_CONTROL_HEIGHT }}
												>
													{SHOW_SEND_TO_PLC && sendPlc.state === "sending"
														? "SENDING…"
														: SHOW_SEND_TO_PLC && sendPlc.state === "confirming"
															? "CONFIRMING…"
															: "SEND TO PLC"}
												</Button>
											) : null}
											{previewRecipeId != null ? (
												<Button
													variant="contained"
													color="success"
													size="small"
													disabled={isSettingCurrent}
													onClick={() => setConfirmCurrentOpen(true)}
													sx={{ height: BANNER_CONTROL_HEIGHT }}
												>
													SET AS DEFAULT
												</Button>
											) : null}
										</Box>
									) : undefined
								}
							/>
							{/* Edit and Create sit at the right edge, as on the other
							    screens. */}
							{canEdit ? (
								<Box sx={{ ml: "auto" }}>
									<RecipeActionsBar
										hasRecipe={!!product && selectedRecipeId != null}
										canCreate={!!product}
										onEdit={openEdit}
										onCreate={openCreate}
									/>
								</Box>
							) : null}
						</Box>

						<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
							<Box sx={{ mb: 0.5 }}>
								<StraightenerTrendChart rows={rows} />
							</Box>

							<Divider sx={{ mb: 0.5 }} />

							<StraightenerSetupGrid rows={rows} recipeLabel={recipeLabel} />
						</Box>
					</>
				)}
			</Paper>

			{/* Recipe editor as a large modal, scoped to the current product. */}
			<Dialog
				open={editOpen && !!product}
				// Close only via the panel's guarded X / Cancel, not backdrop / escape,
				// so unsaved changes aren't lost by an accidental outside click.
				onClose={(_e, reason) => {
					if (reason !== "backdropClick" && reason !== "escapeKeyDown")
						closeEditor();
				}}
				fullWidth
				maxWidth={false}
				slotProps={{
					paper: {
						sx: {
							// Capped: past ~1360px the form stops gaining columns and only
							// stretches the whitespace between label and value.
							width: "min(1360px, 95vw)",
							height: "92vh",
							maxWidth: "none",
							maxHeight: "none",
							m: 0,
							overflow: "hidden",
						},
					},
				}}
			>
				{product ? (
					<InlineRecipeEditor
						product={product}
						versions={currentVersions}
						allNodes={allNodes}
						defaultCrew={crewQ.data ?? ""}
						recipeId={editingRecipeId}
						copySourceRecipeId={selectedRecipeId}
						onRecipeIdChange={handleRecipeIdChange}
						onClose={closeEditor}
						onChanged={refreshRecipes}
					/>
				) : null}
			</Dialog>

			{SHOW_SEND_TO_PLC && currentRecipeId != null ? (
				<SendToPlcModal
					open={sendModalOpen}
					product={product}
					plcSetupTag={banner?.plcSetupTag ?? null}
					recipe={{
						id: currentRecipeId,
						name: banner?.name ?? null,
						version: banner?.version ?? null,
					}}
					rows={data?.rows ?? []}
					state={sendPlc.state}
					matches={sendPlc.matches}
					error={sendPlc.error}
					onSend={sendPlc.send}
					onClose={closeSendModal}
				/>
			) : null}

			<ProductChangeModal
				open={productChangeOpen}
				products={productsQ.data ?? []}
				nodes={allNodes}
				currentProductId={banner?.productSizeId ?? null}
				currentProductLabel={productLabel}
				onChanged={() => {
					setProductChangeOpen(false);
					// The new product brings its own default recipe; a preview of the
					// previous product's recipe no longer applies.
					dropValues("recipeId");
					setNotification("Product changed");
				}}
				onError={setNotification}
				onClose={() => setProductChangeOpen(false)}
			/>

			<SendToPlcConfirmModal
				open={sendConfirmOpen}
				productLabel={productLabel ?? ""}
				recipeLabel={recipeLabel ?? ""}
				onCancel={() => setSendConfirmOpen(false)}
			/>

			<SetCurrentRecipeModal
				open={confirmCurrentOpen}
				productLabel={
					banner ? `(${banner.productTypeName}) ${banner.productName}` : ""
				}
				recipeLabel={
					previewVersion
						? `${previewVersion.Name ?? "(unnamed)"} · v${previewVersion.Version}`
						: ""
				}
				busy={isSettingCurrent}
				onConfirm={() => {
					setConfirmCurrentOpen(false);
					if (previewRecipeId != null) setCurrent(previewRecipeId);
				}}
				onCancel={() => setConfirmCurrentOpen(false)}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
		</Box>
	);
};
